import { Controller } from '@hotwired/stimulus';
import Tab from 'bootstrap/js/src/tab';

export default class extends Controller {
  static targets = ['messages'];

  connect() {
    this.listenForThreadLoad();
  }

  // Fires for the target already in the DOM at connect, and again whenever a Turbo
  // frame navigation swaps the conversation in: on the full-page variant the
  // controller element sits OUTSIDE the frame, so `connect` does not run again and
  // the observer would stay pointed at the removed element.
  messagesTargetConnected(element) {
    this.observeMutations(element);
    // A conversation the user just opened always shows its newest message, however
    // far the page has to travel to get there.
    this.scrollToBottom({ force: true });
  }

  messagesTargetDisconnected() {
    this.observer?.disconnect();
    this.observer = null;
  }

  observeMutations(element) {
    this.observer?.disconnect();
    this.observer = new MutationObserver(() => this.scrollToBottom());
    this.observer.observe(element, {
      childList: true,
      subtree: true,
    });
  }

  scrollToBottom({ force = false } = {}) {
    if (!this.hasMessagesTarget) return;

    // The drawer, and the full-page variant from `lg` up: the messages element is
    // the scroll region, so scrolling it is the whole job.
    if (this.messagesIsScrollable) {
      if (force || this.nearContainerBottom) {
        this.messagesTarget.scrollTop = this.messagesTarget.scrollHeight;
      }
      return;
    }

    this.scrollDocumentToNewest(force);
  }

  // Below `lg` the full-page variant hands scrolling to the document and leaves the
  // messages element `overflow-y: visible`, so a `scrollTop` write clamps to 0 —
  // a long thread would open on its oldest message and streamed chunks would pull
  // nothing down. The chat's own end is scrolled into view rather than the last
  // message's: the composer is sticky at the chat's foot, so aligning the chat's
  // bottom edge lands the newest message directly above it instead of behind it.
  scrollDocumentToNewest(force) {
    if (!force && !this.nearChatEnd) return;

    // `instant`, not `auto`: `auto` defers to CSS, and `<html>` carries
    // `scroll-behavior: smooth`, so every streamed chunk would start an animation —
    // and each new chunk interrupts the last one part-way, leaving the view short of
    // the newest message. Supported from Chrome 97 and Safari 15.4, inside the
    // iOS 15.6 floor.
    this.element.scrollIntoView({ block: 'end', behavior: 'instant' });
  }

  get messagesIsScrollable() {
    const overflowY = getComputedStyle(this.messagesTarget).overflowY;
    return overflowY === 'auto' || overflowY === 'scroll';
  }

  get nearContainerBottom() {
    const el = this.messagesTarget;
    const remaining = el.scrollHeight - el.scrollTop - el.clientHeight;
    return remaining <= el.clientHeight / 2;
  }

  // Reading further up mid-stream beats following the answer down. Half a viewport
  // of slack, so a single large chunk cannot mistake "just streamed" for "scrolled
  // away", while a user actually reading back is well outside it. Both scroll paths
  // consult their own guard: `nearContainerBottom` for the drawer, `nearChatEnd`
  // for the document.
  get nearChatEnd() {
    const remaining =
      this.element.getBoundingClientRect().bottom - window.innerHeight;
    return remaining <= window.innerHeight / 2;
  }

  // Listen for turbo streams that update the chat messages from history
  listenForThreadLoad() {
    this.boundHandleStream = this.handleStreamRender.bind(this);
    document.addEventListener(
      'turbo:before-stream-render',
      this.boundHandleStream
    );
  }

  handleStreamRender(event) {
    const stream = event.target;
    // Check if this stream updates our messages container (loading a past thread)
    if (
      stream.action === 'update' &&
      stream.target?.includes('ai_chat_messages')
    ) {
      // Switch to chat tab after the stream renders
      requestAnimationFrame(() => this.switchToChat());
    }
  }

  switchToChat() {
    const chatTabButton = this.element.querySelector(
      '[data-bs-target="#ai-chat-panel"]'
    );
    if (chatTabButton) {
      const tab = Tab.getOrCreateInstance(chatTabButton);
      tab.show();
    }
  }

  disconnect() {
    this.observer?.disconnect();
    this.observer = null;
    if (this.boundHandleStream) {
      document.removeEventListener(
        'turbo:before-stream-render',
        this.boundHandleStream
      );
    }
  }
}
